import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { RootState } from 'src/app/store/types';
import type {
  GeoLocationRead,
  SourceEnum,
  TagRead,
  TripRequestEmbedRead,
} from 'src/common/external/bambi-api/bambiApi';

export interface SettingState {
  tags: TagRead[];
  selectedTag: TagRead | undefined;
  tagSearchTerm: string;
  showTagsModal: boolean;
  showDeleteTagModal: boolean;
  selectedGeolocation: GeoLocationRead | undefined;
  geolocationSearchTerm: string;
  showGeolocationModal: boolean;
  showArchivedGeolocations: boolean;
  geolocationSourceFilter: SourceEnum[];
  tripRequestEmbedModal: {
    isOpen: boolean;
    tripRequestEmbed?: TripRequestEmbedRead | null;
  };
  tripRequestEmbedDeleteModal: {
    isOpen: boolean;
    tripRequestEmbed?: TripRequestEmbedRead | null;
  };
  tripRequestEmbedCopyModal: {
    isOpen: boolean;
    tripRequestEmbed?: TripRequestEmbedRead | null;
  };
}

export const initialState: SettingState = {
  tags: [],
  selectedTag: undefined,
  tagSearchTerm: '',
  showTagsModal: false,
  showDeleteTagModal: false,
  selectedGeolocation: undefined,
  geolocationSearchTerm: '',
  showGeolocationModal: false,
  showArchivedGeolocations: false,
  geolocationSourceFilter: [],
  tripRequestEmbedModal: {
    isOpen: false,
  },
  tripRequestEmbedDeleteModal: {
    isOpen: false,
  },
  tripRequestEmbedCopyModal: {
    isOpen: false,
  },
};

export const settingSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setSelectedTag: (state, action) => {
      state.selectedTag = action.payload;
    },
    setTagSearchTerm: (state, action) => {
      state.tagSearchTerm = action.payload;
    },
    setShowTagsModal: (state, action) => {
      state.showTagsModal = action.payload;
    },
    setShowDeleteTagModal: (state, action) => {
      state.showDeleteTagModal = action.payload;
    },
    setSelectedGeolocation: (state, action) => {
      state.selectedGeolocation = action.payload;
    },
    setGeolocationSearchTerm: (state, action) => {
      state.geolocationSearchTerm = action.payload;
    },
    setShowGeolocationModal: (state, action) => {
      state.showGeolocationModal = action.payload;
    },
    setShowArchivedGeolocations: (state, action) => {
      state.showArchivedGeolocations = action.payload;
    },
    setGeolocationSourceFilter: (state, action) => {
      state.geolocationSourceFilter = action.payload;
    },
    openTripRequestEmbedModal: (
      state,
      action: PayloadAction<TripRequestEmbedRead>
    ) => {
      state.tripRequestEmbedModal.isOpen = true;
      state.tripRequestEmbedModal.tripRequestEmbed = action.payload;
    },
    closeTripRequestEmbedModal: (state) => {
      state.tripRequestEmbedModal.isOpen = false;
      state.tripRequestEmbedModal.tripRequestEmbed = null;
    },
    createTripRequestEmbed: (state) => {
      state.tripRequestEmbedModal.isOpen = true;
      state.tripRequestEmbedModal.tripRequestEmbed = null;
    },
    deleteTripRequestEmbed: (
      state,
      action: PayloadAction<TripRequestEmbedRead>
    ) => {
      state.tripRequestEmbedDeleteModal.isOpen = true;
      state.tripRequestEmbedDeleteModal.tripRequestEmbed = action.payload;
    },
    closeTripRequestEmbedDeleteModal: (state) => {
      state.tripRequestEmbedDeleteModal.isOpen = false;
      state.tripRequestEmbedDeleteModal.tripRequestEmbed = null;
    },
    openTripRequestEmbedCopyModal: (
      state,
      action: PayloadAction<TripRequestEmbedRead>
    ) => {
      state.tripRequestEmbedCopyModal.isOpen = true;
      state.tripRequestEmbedCopyModal.tripRequestEmbed = action.payload;
    },
    closeTripRequestEmbedCopyModal: (state) => {
      state.tripRequestEmbedCopyModal.isOpen = false;
      state.tripRequestEmbedCopyModal.tripRequestEmbed = null;
    },
  },
});

export const {
  setSelectedTag,
  setTagSearchTerm,
  setShowTagsModal,
  setShowDeleteTagModal,
  setSelectedGeolocation,
  setShowGeolocationModal,
  setGeolocationSearchTerm,
  setShowArchivedGeolocations,
  setGeolocationSourceFilter,
  openTripRequestEmbedCopyModal,
  closeTripRequestEmbedCopyModal,
} = settingSlice.actions;

export const selectGeolocationSearchTerm = (state: RootState) =>
  state.setting.geolocationSearchTerm;

export const selectGeolocationSourceFilter = (state: RootState) =>
  state.setting.geolocationSourceFilter;
